import React from 'react';
import { getStatusColor, getItemStatus } from '../../utils/getStatusColor';

type ActionItemCardProps = {
  title: string;
  icon: React.ReactNode;
  todayCount: number;
  overdueCount: number;
  overdueAmount?: number;
  onClick: () => void;
};

export function ActionItemCard({
  title,
  icon,
  todayCount,
  overdueCount,
  overdueAmount,
  onClick
}: ActionItemCardProps) {
  // Determine status color based on priority (overdue > today > upcoming)
  const status = getItemStatus({ overdue: overdueCount, today: todayCount });
  const hasOverdue = overdueCount > 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm p-3 border-2 hover:shadow-md transition-all active:scale-95 text-center w-full"
      style={{
        borderColor: hasOverdue ? status.color : 'transparent',
      }}
      aria-label={`${title}: ${todayCount} today${overdueCount > 0 ? `, ${overdueCount} overdue` : ''}`}
    >
      {/* Icon - Neutral color, shows entity type */}
      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
        <div className="text-gray-700">{icon}</div>
      </div>

      {/* Title */}
      <div className="text-xs font-medium text-gray-600 mb-2 px-1 leading-tight">
        {title}
      </div>

      {/* Today Count - Color indicates priority/status */}
      <div className="flex items-baseline justify-center gap-1">
        <span
          className="text-2xl font-bold"
          style={{ color: status.color }}
        >
          {todayCount}
        </span>
        <span className="text-xs text-gray-500">today</span>
      </div>

      {/* Overdue Badge - Prominent red accent */}
      {hasOverdue && (
        <div className="mt-2">
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: '#FEF2F0',
              color: '#D97757',
            }}
          >
            {overdueCount} overdue{overdueAmount ? ` • ${formatCurrency(overdueAmount)}` : ''}
          </span>
        </div>
      )}
    </button>
  );
}
